import { useMessageSource } from 'src/i18n/useMessageSource'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { PrinciplesConfigurationSortList } from 'src/screens/administration/configuration-management/principles-configuration/preview/PrinciplesConfigurationSortList'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useSortPrinciples } from 'src/screens/administration/configuration-management/principles-configuration/preview/PrincipleConfigurationSortingUtils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Principle_Config, Query_Root, Subprinciple_Config } from 'src/@types/graphql'
import { queryPrinciplesSortedWithSubprinciplesSorted } from 'src/screens/administration/configuration-management/principles-configuration/principles/principlesConfigQueries'
import { Alert, Box } from '@mui/material'
import { debounce } from 'lodash'

export const PrinciplesConfigurationPreviewPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const { switchPrinciplesPosition, switchSubprinciplesPosition } = useSortPrinciples()

  // state
  const [principlesSorting, setPrinciplesSorting] = useState<Principle_Config[]>([])
  const [isReorderingLocked, setIsReorderingLocked] = useState(false)

  // fetch data
  const fetchPrinciples = useCallback(async () => {
    const { data, error } = await urqlClient
      .query<{ principle_config: Query_Root['principle_config'] }>(queryPrinciplesSortedWithSubprinciplesSorted, {})
      .toPromise()

    if (error || !data?.principle_config) {
      throw new Error('Resource not found')
    }

    const fetchedPrincipleConfigs = data.principle_config
    return { fetchedPrincipleConfigs }
  }, [urqlClient])

  const initPrinciplesSorting = useCallback(() => {
    fetchPrinciples()
      .then((res) => {
        setPrinciplesSorting(res.fetchedPrincipleConfigs)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setIsReorderingLocked(false)
      })
  }, [fetchPrinciples, notificationService])

  useEffect(() => {
    initPrinciplesSorting()
  }, [initPrinciplesSorting])

  // handlers
  const debouncedHandleReorder = useMemo(
    () =>
      debounce(
        async (reorderFunction: (...args: any[]) => Promise<void>, ...args: any[]) => {
          if (isReorderingLocked) return

          setIsReorderingLocked(true)

          try {
            await reorderFunction(...args)
            await new Promise((resolve) => setTimeout(resolve, 500))
          } catch (e) {
            setIsReorderingLocked(false)
            console.error('Error caught in principles sort debounce handler reorder method')
          }
        },
        500,
        { leading: true, trailing: false },
      ),
    [],
  )

  const onSwitchPrinciplesPositionInternal = useCallback(
    async (pc1: Principle_Config, pc2: Principle_Config) => {
      try {
        await switchPrinciplesPosition(pc1, pc2)
        initPrinciplesSorting()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [initPrinciplesSorting, notificationService, switchPrinciplesPosition],
  )

  const onSwitchSubprinciplesPositionInternal = useCallback(
    async (spc1: Subprinciple_Config, spc2: Subprinciple_Config) => {
      try {
        await switchSubprinciplesPosition(spc1, spc2)
        initPrinciplesSorting()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [initPrinciplesSorting, notificationService, switchSubprinciplesPosition],
  )

  const onSwitchPrinciplesPosition = useCallback(
    async (pc1: Principle_Config, pc2: Principle_Config) => {
      await debouncedHandleReorder(onSwitchPrinciplesPositionInternal, pc1, pc2)
    },
    [debouncedHandleReorder, onSwitchPrinciplesPositionInternal],
  )

  const onSwitchSubprinciplesPosition = useCallback(
    async (spc1: Subprinciple_Config, spc2: Subprinciple_Config) => {
      await debouncedHandleReorder(onSwitchSubprinciplesPositionInternal, spc1, spc2)
    },
    [debouncedHandleReorder, onSwitchSubprinciplesPositionInternal],
  )

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.principles')} ${getMessage('label.configuration')}`}
    >
      <PageLayout>
        <Box pb={2}>
          <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
        </Box>
        <PrinciplesConfigurationSortList
          principlesSorting={principlesSorting}
          onSwitchPrinciplePosition={onSwitchPrinciplesPosition}
          onSwitchSubprinciplePosition={onSwitchSubprinciplesPosition}
          isReorderingLocked={isReorderingLocked}
        />
      </PageLayout>
    </ScreenLayout>
  )
}
